// shared-lib/cabinet-runs/sinkUnits.js
//
// What stands under a sink in a run whose line has a size catalog (Venus).
// The user, 2026-10-30: a sink bay offers that line's SINK cabinets only —
// never a drawer base or plain doors — and, where the line makes more than one
// sink cabinet at the bay's width, each of them. Venus's 30" and 36" come as a
// plain sink base (VSB: a false drawer front over doors) or with a bank of
// drawers on the left or right (VSD…L / R). Every Venus sink cabinet has doors
// under the bowl; none is drawers only, so the old "switch the sink to
// drawers" priced the sink cabinet and drew a drawer base Venus does not make.
//
// A generic line keeps every front under a sink: a drawer base around the
// plumbing of a vessel sink is a real thing, and the user's call there.
//
// The one rule the panel's Type picker and its Sink cabinet row, the bay
// write, the fill and the drawing ask. The choice rides the bay record as
// `sinkDrawers: 'left' | 'right'` and is stamped on the box the fill builds
// only while the line makes that cabinet at its width — like a corner choice,
// honoured while it fits and kept for when it fits again.
//
// Pure.

const { familyOf } = require('./families');
const { familySizes, resolveCabinetSku } = require('./vendorSizes');
const { vendorPartName } = require('./vendorUnitWords');

const IN = 0.0254;
const SINK_FRONT = 'sink';
const SINK_DRAWER_SIDES = Object.freeze(['left', 'right']);
const CHOICE_NAMES = Object.freeze({
  none: 'No drawers',
  left: 'Drawers left',
  right: 'Drawers right',
});

/** 'left' | 'right', or null for anything else (the plain sink base). */
const normalizeSinkDrawers = (v) => (SINK_DRAWER_SIDES.includes(v) ? v : null);

/** Does this run's line sell its own sink cabinets (it has a size catalog)? */
const lineSellsSinkCabinets = (run) => !!familySizes(familyOf(run || {}));

/**
 * Why a sink bay of this run cannot wear `frontId`, as a sentence — or null.
 * Only a SINK bay (`bayKind` 'sink-base', the run engine's kind for a bay
 * holding a basin) on a line with a catalog refuses anything: there, only the
 * sink front.
 */
function sinkBayFrontReason(run, bayKind, frontId) {
  if (bayKind !== 'sink-base' || frontId === SINK_FRONT) return null;
  if (!lineSellsSinkCabinets(run)) return null;
  return 'Only sink cabinets may be used under a sink';
}

/**
 * The front the fill builds a sink bay with: the sink front on a line that
 * sells its own sink cabinets, whatever an older record asks; the bay's
 * chosen front on any other line. Null when nothing was chosen.
 */
function chosenSinkBayFront(run, record) {
  if (lineSellsSinkCabinets(run)) return null;
  return record && record.type ? record.type : null;
}

/** A sink box as it would be with drawers to `side` (null: none). */
function withSide(box, side) {
  const probe = { ...box };
  if (side) probe.sinkDrawers = side;
  else delete probe.sinkDrawers;
  return probe;
}

/**
 * The line's sink cabinets this sink box could be at its width and bowl
 * count, the plain one first: `[{ id: 'none'|'left'|'right', sku, name,
 * title }]`. Empty for a generic line or a box that is no sink base; one
 * entry where the line makes one cabinet there (the 60" double, every VSB
 * width without a handed unit).
 */
function sinkUnitChoices(family, box, ctx = {}) {
  if (!familySizes(family) || !box?.category?.sinkBase) return [];
  const choices = [];
  for (const side of [null, ...SINK_DRAWER_SIDES]) {
    const { sku } = resolveCabinetSku(family, withSide(box, side), ctx);
    if (!sku || choices.some((c) => c.sku === sku)) continue;
    const id = side || 'none';
    choices.push({
      id,
      sku,
      name: CHOICE_NAMES[id],
      title: vendorPartName(family, sku),
    });
  }
  return choices;
}

/**
 * Put the side the governing bay record asks for on a sink box the fill just
 * built — or take it off: only where the line makes that cabinet at the box's
 * width. `delete`, never `= undefined` (Firestore). Mutates, like the
 * appearance pass.
 */
function stampSinkDrawers(cabinet, record, family, ctx = {}) {
  const side = normalizeSinkDrawers(record?.sinkDrawers);
  delete cabinet.sinkDrawers;
  if (!side || !familySizes(family)) return;
  if (resolveCabinetSku(family, withSide(cabinet, side), ctx).sku) {
    cabinet.sinkDrawers = side;
  }
}

/**
 * The columns the line's sink cabinet for `cabinet` is drawn with, scaled to
 * the box — or null for a plain sink base (its one `sink-front` column is
 * already the part), a line that draws none, or a box it makes no part for.
 */
function sinkUnitColumns(family, cabinet, ctx = {}) {
  const sizes = familySizes(family);
  const width = cabinet?.dimensions?.x;
  if (!sizes?.sinkFronts || !(width > 0)) return null;
  const { sku } = sizes.resolveSku(cabinet, ctx);
  const spec = sku && sizes.sinkFronts[sku];
  if (!spec) return null;
  const scale = width / (spec.reduce((s, c) => s + c.w, 0) * IN);
  return spec.map(({ w, sideDrawers, ...column }) => ({
    ...column,
    width: w * IN * scale,
    ...(sideDrawers ? { sideDrawerWidth: sideDrawers * IN * scale } : {}),
  }));
}

/**
 * A vendor line's sink bases drawn as the part it sells: Venus's 60" double
 * with its drawers between the basins, a 36" with its drawers to one side.
 * Every lower the fill built passes here once (as `vendorDrawerStacks`); only
 * a box wearing the one sink front is redrawn. Mutates.
 */
function vendorSinkFronts(cabinets, run, profileId) {
  const family = familyOf(run || {});
  if (!familySizes(family)) return;
  for (const cabinet of cabinets) {
    const columns = cabinet.columns || [];
    if (columns.length !== 1 || columns[0].type !== 'sink-front') continue;
    const drawn = sinkUnitColumns(family, cabinet, { profileId });
    if (drawn) cabinet.columns = drawn;
  }
}

module.exports = {
  SINK_DRAWER_SIDES,
  normalizeSinkDrawers,
  lineSellsSinkCabinets,
  sinkBayFrontReason,
  chosenSinkBayFront,
  sinkUnitChoices,
  stampSinkDrawers,
  sinkUnitColumns,
  vendorSinkFronts,
};
